import {
  cloneObject,
  getUserInitialsForBubble,
  getPlantLastModifyByIcon,
  getPlantLastModifyByLabel,
} from '@/core/utils/utils';
import {
  SET_APP_LOADING,
  SET_USER_PERMISSIONS,
  SET_CONTEXTUAL_MENU_ITEMS,
  SET_SELECTED_ENTRANCE_PLANT,
  SET_SELECTED_ENTRANCE_PLANT_ID,
  SET_ADMIN_VIEW,
  SET_SYNC_INFO,
  SET_OFFLINE_PLANT,
} from '@/core/store/action-types';
import { mapGetters, mapState } from 'vuex';
import plantsClient from '@/core/services/plants';
import { Vue, Options } from 'vue-class-component';
import {
  PlantType,
  EntrancePlantDetailDTO,
  EpUserRole,
  PlantSharingDTO,
  SyncInfo,
} from '@/../client-generator/generated-clients/plants';
import MenuEPSharing from './MenuEPSharing/MenuEPSharing.vue';
import MegaMenuItem, { MenuItemRoute } from '@/core/models/menu-items';
import { FETCH_PLANT, HANDLE_REFRESH_PLANT } from '@/core/utils/emitter-events';
import DialogPlantCreate from '../PlantsHub/DialogPlantCreate/DialogPlantCreate.vue';
import ConfirmPlantUpdated from '../PlantDetail/ConfirmPlantUpdated/ConfirmPlantUpdated.vue';
import DialogPlantDuplicate from '../PlantsHub/DialogPlantDuplicate/DialogPlantDuplicate.vue';
import DialogConfirmDeletePlant from '@/views/components/DialogConfirmDeletePlant/DialogConfirmDeletePlant.vue';
import DialogConfirmTransferOwnership from '@/views/components/DialogConfirmTransferOwnership/DialogConfirmTransferOwnership.vue';
import { AccountInfo } from '@azure/msal-browser';

@Options({
  name: 'EntrancePlantDetail',
  watch: {
    menuItemsContextualWithActiveClasses(newValue) {
      this.$store.dispatch(SET_CONTEXTUAL_MENU_ITEMS, newValue);
    },
    "syncInfo.currentlySyncing": function(newValue) {
      if(newValue) {
        this.isPausedFetchupdate = newValue;
        this.registerSyncingCheck();
      }
    }
  },
  components: {
    MenuEPSharing,
    DialogPlantCreate,
    ConfirmPlantUpdated,
    DialogPlantDuplicate,
    DialogConfirmDeletePlant,
    DialogConfirmTransferOwnership,
  },
  computed: {
    ...mapState(['currentEntrancePlant', 'account', 'syncInfo']),
  },
})
export default class AppResourceEntrancePlantDetail extends Vue {
  PlantType = PlantType;
  plantEditEnabled: boolean = true;
  currentEntrancePlant!: EntrancePlantDetailDTO;
  selectedPlantId: number | null = null;
  isPlantDuplicateDialogVisible = false;
  tempPlant: EntrancePlantDetailDTO | null = null;
  account!: AccountInfo;
  usersPermissions: EpUserRole[] = [];
  plantUpdateKey = 0;
  syncInfo!: SyncInfo;
  intervalCheckSync: any | null = null;
  currentUserPerms: EpUserRole | null = null;

  get menuItemsContextual(): MegaMenuItem[] {
    return [
      {
        label: this.$t('AppResource.Plant'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/icons/icon-general-view.svg')});`,
        to: {
          name: 'page-entranceplant-dashboard',
        },
      },
      {
        label: this.$t('AppResourceEP.AddressBookResidents'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/entrancePlants/icon-resident.svg')});`,
        to: {
          name: 'page-entranceplant-residents',
        },
      },
      {
        label: this.$t('AppResourceEP.Badges'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/entrancePlants/icon-badge.svg')});`,
        to: {
          name: 'page-entranceplant-badges',
        },
      },
      {
        class: 'p-menuitem-main p-menuitem-custom-icon no-label',
        style: `--menuitem-icon-url: url(${require('@/assets/images/icons/icon-add.svg')});`,
        subroutes: ['page-entranceplant-guests', 'page-entranceplant-messages'],
        items: [
          [
            {
              items: [
                {
                  label: this.$t('AppResourceEP.Guests'),
                  class: 'p-menuitem-custom-icon',
                  style: `--menuitem-icon-url: url(${require('@/assets/images/entrancePlants/icon-guest.svg')});`,
                  to: {
                    name: 'page-entranceplant-guests',
                  },
                },
                {
                  label: this.$t('AppResourceEP.Messages'),
                  class: 'p-menuitem-custom-icon',
                  style: `--menuitem-icon-url: url(${require('@/assets/images/entrancePlants/icon-message.svg')});`,
                  to: {
                    name: 'page-entranceplant-messages',
                  },
                },
              ],
            },
          ],
        ],
      },
      {
        label: this.$t('AppResourceEP.Switchboard'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        style: `--menuitem-icon-url: url(${require('@/assets/images/entrancePlants/icon-switchboard.svg')});`,
        to: {
          name: 'page-entranceplant-switchboards',
        },
      },
      {
        label: this.$t('AppResourceEP.ParametersAndSettings'),
        class: 'p-menuitem-main p-menuitem-custom-icon',
        icon: `${require('@/assets/images/entrancePlants/icon-parameters.svg')}`,
        style: `--menuitem-icon-url: url(${require('@/assets/images/entrancePlants/icon-parameters.svg')});`,
        to: {
          name: 'page-entranceplant-settings',
        },
      },
    ];
  }

  isPlantDirty = false;
  isVisibleEditDialog = false;
  isDevicesCardLoading = false;
  isVisibleDeletePlantConfirm = false;
  isVisibleConfirmPlantUpdated = false;
  isVisibleAddRoomObjectDialog = false;
  isVisibleCreateStructureDialog = false;
  isVisibleTransferOwnershipConfirm = false;

  isPausedFetchupdate = false;
  intervalFetchUpdate: any | null = null;
  timeoutRenewSession: any | null = null;

  get iconDevice() {
    return getPlantLastModifyByIcon(
      this.currentEntrancePlant?.lastModifyByDesktop
    );
  }

  get iconDeviceLabel() {
    return getPlantLastModifyByLabel(
      this.currentEntrancePlant?.lastModifyByDesktop
    );
  }

  get menuItemsContextualWithActiveClasses(): MegaMenuItem[] {
    return this.menuItemsContextual.map((menuItem) => {
      let additionalClass = '';
      if (menuItem.to && (menuItem.to as MenuItemRoute).name) {
        additionalClass = this.getMenuItemClass(
          (menuItem.to as MenuItemRoute).name
        );
      }

      if (menuItem.subroutes) {
        additionalClass = this.getMenuItemClassSubmenu(menuItem.subroutes);
      }

      return {
        ...menuItem,
        class: `${menuItem.class} ${additionalClass}`,
      };
    });
  }

  get username() {
    return `${this.currentEntrancePlant?.lastModifyBy?.name} ${this.currentEntrancePlant?.lastModifyBy?.surname}`;
  }

  get userInitials() {
    return getUserInitialsForBubble(this.currentEntrancePlant.lastModifyBy);
  }

  get hideFooter() {
    return [
      'page-entranceplant-guests',
      'page-entranceplant-badges',
      'page-entranceplant-messages',
      'page-entranceplant-settings',
      'page-entranceplant-residents',
      'page-entranceplant-structure-building',
      'page-entranceplant-structure-riser',
      'page-entranceplant-switchboards',
    ].includes(this.$route.name as string);
  }

  get favouriteIcon() {
    return require(`@/assets/images/icons/icon-favourite${
      !this.currentEntrancePlant?.isFavourite ? '-outline' : ''
    }.svg`);
  }

  get plantName() {
    if (this.currentEntrancePlant) {
      return this.currentEntrancePlant.name;
    }

    return '';
  }

  get plantDeleteEnabled() {
    if (this.currentEntrancePlant && this.currentUserPerms) {
      return !this.currentUserPerms.isOnlyBuildingAdmin;
    }
    return false;
  }

  forceRerender = () => {
    if(!this.currentEntrancePlant?.lastModifyByDesktop) {
      this.plantUpdateKey += 1;
    }
  };

  created() {
    this.$store.dispatch(SET_SELECTED_ENTRANCE_PLANT, null);
    this.$store.dispatch(SET_USER_PERMISSIONS, null);
    this.$store.dispatch(SET_ADMIN_VIEW, false);
    this.$store.dispatch(SET_OFFLINE_PLANT, false);
    this.$store.dispatch(SET_SYNC_INFO, null);
  }

  unmounted() {
    this.$store.dispatch(SET_SELECTED_ENTRANCE_PLANT, null);
    this.$store.dispatch(SET_USER_PERMISSIONS, null);
    this.$store.dispatch(SET_ADMIN_VIEW, false);
    this.$store.dispatch(SET_OFFLINE_PLANT, false);
    this.$store.dispatch(SET_SYNC_INFO, null);
  }

  async mounted() {
    this.$store.dispatch(
      SET_CONTEXTUAL_MENU_ITEMS,
      this.menuItemsContextualWithActiveClasses
    );

    this.selectedPlantId = parseInt(this.$route.params.plantId.toString(), 10);
    this.$store.dispatch(SET_SELECTED_ENTRANCE_PLANT_ID, this.selectedPlantId);
    this.$store.dispatch(SET_APP_LOADING, true);
    this.usersPermissions = await plantsClient.userspermissions(
      this.selectedPlantId
    );
    this.$store.dispatch(SET_APP_LOADING, false);

    await this.fetchPlant();

    this.registerEmitterEvents();
    this.registerPeriodicFetchUpdates();
    this.renewSessionTimeout();
  }

  beforeUnmount() {
    this.unregisterEmitterEvents();
    this.unregisterPeriodicFetchUpdates();
    this.unregisterRenewSessionTimeout();

    this.$store.dispatch(SET_SELECTED_ENTRANCE_PLANT_ID, null);
  }

  async fetchPlant() {
    if (!this.selectedPlantId) return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);

      const plant = await plantsClient.entranceplantGET(
        this.selectedPlantId,
        undefined
      );

      const usersList = { owner: plant.owner, shared: plant.sharedWith };
      const currentUserEmail =
        usersList.owner?.id === this.account.localAccountId
          ? usersList.owner.email
          : usersList.shared?.find(
              (u) => u.userId === this.account.localAccountId
            )?.email;
      this.currentUserPerms =
        this.usersPermissions.find((user) => user.email === currentUserEmail) ??
        null;

      const isOfflinePlant = plant.syncStatus?.isPlantActive && !plant.syncStatus?.connected;
      
      this.$store.dispatch(SET_OFFLINE_PLANT, isOfflinePlant);
      this.$store.dispatch(SET_SYNC_INFO, plant.syncStatus?.syncInfo);
      this.$store.dispatch(SET_ADMIN_VIEW, this.currentUserPerms?.isOnlyBuildingAdmin);
      this.$store.dispatch(SET_USER_PERMISSIONS, this.currentUserPerms);
      this.$store.dispatch(SET_SELECTED_ENTRANCE_PLANT, plant);
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    } finally {
      if(!this.syncInfo || !this.syncInfo.currentlySyncing) {
        this.$store.dispatch(SET_APP_LOADING, false);
      }
    }
  }

  registerEmitterEvents() {
    this.$emitter.on(FETCH_PLANT, this.fetchPlant);
    this.$emitter.on(
      HANDLE_REFRESH_PLANT,
      this.handleRefreshPlantFromOuterEvent
    );
  }

  unregisterEmitterEvents() {
    this.$emitter.off(FETCH_PLANT, this.fetchPlant);
    this.$emitter.off(
      HANDLE_REFRESH_PLANT,
      this.handleRefreshPlantFromOuterEvent
    );
  }

  registerPeriodicFetchUpdates() {
    this.intervalFetchUpdate = setTimeout(async () => {
      await this.doFetchUpdatesInternal();
      this.registerPeriodicFetchUpdates();
    }, this.$config.PLANT_REFRESH_INTERVAL);
  }

  unregisterPeriodicFetchUpdates() {
    clearTimeout(this.intervalFetchUpdate);
  }

  private async doFetchUpdatesInternal() {
    if (this.isPausedFetchupdate) {
      return;
    }

    try {
      const newPlant = await plantsClient.entranceplantGET(
        this.selectedPlantId as number,
        this.currentEntrancePlant?.lastModify
      );

      if (newPlant && newPlant.desktopId && !newPlant.lastModifyByDesktop) {
        this.isVisibleConfirmPlantUpdated = true;
        this.isPausedFetchupdate = true;
        this.tempPlant = newPlant;
      }
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    }
  }

  async renewSessionTimeout() {
    if (!this.selectedPlantId) return;
    try {
      var renewSecondsDto = await plantsClient.session(this.selectedPlantId);
      this.timeoutRenewSession = setTimeout(
        this.renewSessionTimeout,
        (renewSecondsDto.renewSessionSeconds ?? 60) * 1000
      );
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    }
  }

  unregisterRenewSessionTimeout() {
    clearTimeout(this.timeoutRenewSession);
  }

  registerSyncingCheck() {
    this.intervalCheckSync = setInterval(async () => {
      await this.doCheckSyncStatus();
    }, 3000);
  }

  private async doCheckSyncStatus() {
    if (!!this.syncInfo && this.syncInfo.currentlySyncing) {
      try {
        const syncStatus = await plantsClient.syncstatus(this.currentEntrancePlant.desktopId as number);
        
        if (!!syncStatus && !syncStatus.currentlySyncing || syncStatus.isFailed) {
          await plantsClient.virtualdevices(this.currentEntrancePlant.desktopId as number);
          this.$store.dispatch(SET_SYNC_INFO, syncStatus);
          this.fetchPlant();
        }
      } catch (error) {
        this.$store.dispatch(SET_SYNC_INFO, null);
        this.fetchPlant();
        console.log(error);
      }
    }
    else {
      this.forceRerender();
      this.unregisterSyncingCheck();
    }
  }
  
  unregisterSyncingCheck() {
    clearInterval(this.intervalCheckSync);
    this.intervalCheckSync = null;
    this.$store.dispatch(SET_APP_LOADING, false);
    this.isPausedFetchupdate = false;
  }

  handleRefreshPlantFromOuterEvent() {
    this.isPausedFetchupdate = true;
    this.isVisibleConfirmPlantUpdated = true;
  }

  getMenuItemClass(routeName: string) {
    if (this.$route.name === routeName) {
      return 'p-menuitem-active';
    }

    return '';
  }

  getMenuItemClassSubmenu(subroutesNames: string[]) {
    if (subroutesNames.includes(this.$route.name as string)) {
      return 'submenu-active';
    }

    return '';
  }

  toggleEditDialog() {
    this.isVisibleEditDialog = !this.isVisibleEditDialog;
  }

  getParsedDate(date?: Date) {
    if (!date) return '';
    const options: any = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleString(navigator.language, options);
  }

  toggleDeletePlantConfirm() {
    this.isVisibleDeletePlantConfirm = !this.isVisibleDeletePlantConfirm;
  }

  async handleDeletePlant() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      if (!this.plantDeleteEnabled) {
        throw new Error('User permissions not found or insufficient');
      }

      await plantsClient.plantDELETE(
        this.currentEntrancePlant?.desktopId as number
      );
      this.$router.push({
        name: 'page-plants-hub',
      });
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async handleDuplicatePlant() {}

  handleDuplicatePlantFull() {}

  async hideConfirmPlantUpdated() {
    this.isVisibleConfirmPlantUpdated = false;

    if (!this.tempPlant) {
      // Handle refresh plant triggered by
      // an event (API call) from another
      // (sub)component
      await this.fetchPlant();
    } else {
      // Handle refresh plant triggered by
      // the periodic API call for
      // checking if the plant was updated.
      // The plant was already fetched from
      // the aforementioned API call and
      // stored in tempPlant.

      this.$store.dispatch(
        SET_SELECTED_ENTRANCE_PLANT,
        EntrancePlantDetailDTO.fromJS(cloneObject(this.tempPlant))
      );
      this.forceRerender();
      this.tempPlant = null;
    }

    this.isPausedFetchupdate = false;
  }

  handleInvitedUser(userEmail: string) {
    const newUser = new PlantSharingDTO({
      invitedEmail: userEmail,
      confirmed: false,
    });

    this.currentEntrancePlant?.sharedWith?.push(newUser);
    setTimeout( async() => {
    this.usersPermissions = await plantsClient.userspermissions(
      this.selectedPlantId!
    ), 1000});
  }

  handleRemoveSharingUser(user: PlantSharingDTO) {
    const userIndex = this.currentEntrancePlant?.sharedWith?.findIndex(
      (u) => u.userId === user.userId
    );

    if (userIndex != null && userIndex >= 0) {
      this.currentEntrancePlant?.sharedWith?.splice(userIndex, 1);
    }

    setTimeout( async() => {
      this.usersPermissions = await plantsClient.userspermissions(
        this.selectedPlantId!
    ), 1000});
  }

  async togglePlantFromFavorites() {
    if (!this.currentEntrancePlant?.desktopId) return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      this.currentEntrancePlant.isFavourite = await plantsClient.togglePlantFromFavorites(
        this.currentEntrancePlant.desktopId
      );
      this.currentEntrancePlant.lastModify = new Date();
    } catch (error) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  onSaveAndExitClick(): void {
    this.$router.replace({
      path: '/',
      replace: true,
    });
  }

  async handleHidePlantDuplicateDialog() {}

  async handlePlantDuplicated() {}
}
